import React, { useState, useEffect } from "react"
import { useMutation } from "urql"

import { useToast } from "../../../../contexts/ToastContext"
import { useWebsiteBuilderV2 } from "../../WebsiteBuilderV2Context"
import ImagePickerV2 from "../shared/ImagePickerV2"

import EditorFooter from "./shared/EditorFooter"

// ColorInput component (matches builder editors styling)
const ColorInput = ({ label, value, onChange, className = "" }) => (
  <div className={`space-y-1 ${className}`}>
    <label className="block text-sm font-medium text-builder-primary">{label}</label>
    <div className="flex gap-2">
      <input
        type="color"
        value={value || "#ffffff"}
        onChange={(e) => onChange(e.target.value)}
        className="h-8 w-12 cursor-pointer rounded border border-builder-border"
      />
      <input
        type="text"
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder="#ffffff"
        className="flex-1 rounded border border-builder-border px-2 py-1 text-sm text-builder-primary focus:border-builder-accent focus:outline-none"
      />
    </div>
  </div>
)

const CTABannerEditor = ({ section, practice, onSave, onCancel, onDelete, isEditing = false }) => {
  const { handleUpdateSectionPreview, clearPreview, pages } = useWebsiteBuilderV2()
  const { showToast } = useToast()

  const [formData, setFormData] = useState({
    headline: section?.headline || "Ready to Take the Next Step?",
    subheadline: section?.subheadline || "Learn more about us",
    body: section?.body || "",
    settings: section?.settings || {
      buttonText: "Get Started",
      buttonAction: "booking",
      backgroundColor: "#0891b2",
      titleColor: "#ffffff",
      subheadlineColor: "#ffffff",
      descriptionColor: "#ffffff",
      showIcon: false,
      iconType: "arrow", // arrow, phone, calendar, email
      backgroundImageUrl: ""
    }
  })
  const [errors, setErrors] = useState({})

  // GraphQL mutation for uploading images (match CTAEditor)
  const UPLOAD_IMAGE_MUTATION = `
    mutation UploadImage($handle: String!, $description: String!, $provider: String) {
      uploadImage(handle: $handle, description: $description, provider: $provider) {
        result
        errors
        photoUrl
        altText
      }
    }
  `
  const [{ fetching: uploadFetching }, uploadImage] = useMutation(UPLOAD_IMAGE_MUTATION)

  useEffect(() => {
    if (section) {
      setFormData({
        headline: section.headline || "Ready to Take the Next Step?",
        subheadline: section.subheadline || "",
        body: section.body || "",
        settings: {
          buttonText: "Get Started",
          buttonAction: "booking",
          backgroundColor: "#0891b2",
          titleColor: "#ffffff",
          subheadlineColor: "#ffffff",
          descriptionColor: "#ffffff",
          showIcon: false,
          iconType: "arrow",
          backgroundImageUrl: "",
          ...section.settings
        }
      })
    }
  }, [section, practice])

  const handleChange = (field, value) => {
    setFormData((prev) => {
      const updated = { ...prev, [field]: value }

      // Update preview immediately for content changes
      if (section?.id && ["headline", "subheadline", "body"].includes(field)) {
        handleUpdateSectionPreview(section.id, {
          ...section,
          ...updated
        })
      }

      return updated
    })

    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: null }))
    }
  }

  const handleSettingsChange = (key, value) => {
    setFormData((prev) => ({
      ...prev,
      settings: { ...prev.settings, [key]: value }
    }))

    // Update preview immediately for settings changes
    if (
      section?.id &&
      [
        "buttonText",
        "buttonAction",
        "backgroundColor",
        "titleColor",
        "subheadlineColor",
        "descriptionColor",
        "showIcon",
        "iconType",
        "backgroundImageUrl",
        "showBackgroundImage",
        "ctaTarget",
        "pageSlug"
      ].includes(key)
    ) {
      const updatedSettings = { ...formData.settings, [key]: value }
      handleUpdateSectionPreview(section.id, {
        ...section,
        ...formData,
        settings: updatedSettings
      })
    }
  }

  // Image selection handler for ImagePickerV2
  const handleBackgroundImageSelect = ({ handle, provider }) => {
    uploadImage({
      handle: handle,
      description: "cta_section_image",
      provider: provider
    })
      .then((result) => {
        if (result?.data?.uploadImage?.result === "success") {
          handleSettingsChange("backgroundImageUrl", result.data.uploadImage.photoUrl)
          showToast("Image uploaded successfully!")
        } else {
          // eslint-disable-next-line no-console
          console.error(result)
          let errorMessage = "There was an error uploading your image. Please try again later or contact support."
          if (result.data?.uploadImage?.errors) errorMessage += ` ${result.data.uploadImage.errors}`
          showToast({ type: "error", content: errorMessage })
        }
      })
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.error("Upload error:", error)
        showToast({ type: "error", content: "There was an error uploading your image. Please try again." })
      })
  }

  const handleBackgroundImageRemove = () => {
    handleSettingsChange("backgroundImageUrl", "")
    showToast("Image removed.")
  }

  // Close button - discard changes and close immediately
  const handleCancelClick = () => {
    clearPreview()
    onCancel()
  }

  const validateForm = () => {
    const newErrors = {}

    // Allow empty headline - user can remove content if they want
    // if (!formData.headline.trim()) {
    //   newErrors.headline = "Headline is required"
    // }

    // Allow empty button text - user can remove content if they want
    // if (!formData.settings.buttonText.trim()) {
    //   newErrors.buttonText = "Button text is required"
    // }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = (e) => {
    e?.preventDefault?.()
    if (validateForm()) {
      onSave({
        layout: "cta_banner",
        headline: formData.headline,
        subheadline: formData.subheadline,
        body: formData.body,
        settings: formData.settings
      })
    }
  }

  const actionOptions = [
    { value: "page", label: "Link to Page" },
    { value: "booking", label: "Open Booking" },
    { value: "contact", label: "Go to Contact" },
    { value: "phone", label: "Call Phone" },
    { value: "email", label: "Send Email" },
    { value: "external", label: "External Link" }
  ]

  const iconOptions = [
    { value: "arrow", label: "Arrow" },
    { value: "phone", label: "Phone" },
    { value: "calendar", label: "Calendar" },
    { value: "email", label: "Email" },
    { value: "star", label: "Star" }
  ]

  return (
    <>
      {/* Form Content (Figma-aligned styling) */}
      <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-6 px-8 pt-6">
        {/* Headline */}
        <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-6 p-0">
          <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-0.5 p-0 text-left leading-[0]">
            <div className="relative w-full shrink-0 font-['Manrope:Bold',_sans-serif] text-[16px] font-bold text-builder-primary">
              <p className="block leading-[24px]">Headline</p>
            </div>
            <div className="relative w-full shrink-0 font-['Manrope:Regular',_sans-serif] text-[14px] font-normal text-builder-secondary">
              <p className="block leading-[20px]">
                Create a powerful headline to capture attention and communicate your core message.
              </p>
            </div>
          </div>
          <div className="relative w-full max-w-[432px] shrink-0 rounded-lg bg-white">
            <input
              type="text"
              value={formData.headline}
              onChange={(e) => handleChange("headline", e.target.value)}
              placeholder="Ready to Take the Next Step?"
              className="relative box-border flex w-full flex-row content-stretch items-center justify-between text-clip rounded-lg border border-builder-border px-3 py-2 font-['Manrope:Regular',_sans-serif] text-[16px] font-normal leading-[24px] text-builder-primary focus:border-builder-accent focus:outline-none"
              required
            />
          </div>
          {errors.headline && (
            <div className="relative shrink-0 text-left font-['Manrope:Regular',_sans-serif] text-[13px] font-normal leading-[0] text-red-500">
              <p className="block leading-[19.5px]">{errors.headline}</p>
            </div>
          )}
        </div>

        {/* Subheadline */}
        <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-6 p-0">
          <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-0.5 p-0 text-left leading-[0]">
            <div className="relative w-full shrink-0 font-['Manrope:Bold',_sans-serif] text-[16px] font-bold text-builder-primary">
              <p className="block leading-[24px]">Subheadline</p>
            </div>
            <div className="relative w-full shrink-0 font-['Manrope:Regular',_sans-serif] text-[14px] font-normal text-builder-secondary">
              <p className="block leading-[20px]">Create a concise supporting message.</p>
            </div>
          </div>
          <div className="relative w-full max-w-[432px] shrink-0 rounded-lg bg-white">
            <textarea
              value={formData.subheadline}
              onChange={(e) => handleChange("subheadline", e.target.value)}
              placeholder="Brief supporting text"
              rows={2}
              className="relative box-border w-full resize-none rounded-lg border border-builder-border px-3 py-2 font-['Manrope:Regular',_sans-serif] text-[16px] leading-[24px] text-builder-primary focus:border-builder-accent focus:outline-none"
            />
          </div>
        </div>

        {/* Body Content */}
        <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-6 p-0">
          <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-0.5 p-0 text-left leading-[0]">
            <div className="relative w-full shrink-0 font-['Manrope:Bold',_sans-serif] text-[16px] font-bold text-builder-primary">
              <p className="block leading-[24px]">Body Content</p>
            </div>
            <div className="relative w-full shrink-0 font-['Manrope:Regular',_sans-serif] text-[14px] font-normal text-builder-secondary">
              <p className="block leading-[20px]">Add detailed content or description for your banner.</p>
            </div>
          </div>
          <div className="relative w-full max-w-[432px] shrink-0 rounded-lg bg-white">
            <textarea
              value={formData.body}
              onChange={(e) => handleChange("body", e.target.value)}
              placeholder="Enter your banner content here..."
              rows={4}
              className="relative box-border w-full resize-none rounded-lg border border-builder-border px-3 py-2 font-['Manrope:Regular',_sans-serif] text-[16px] leading-[24px] text-builder-primary focus:border-builder-accent focus:outline-none"
            />
          </div>
        </div>

        {/* CTA section */}
        <div className="relative box-border flex w-full max-w-[432px] shrink-0 flex-col content-stretch items-start justify-start gap-6 p-0">
          <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-0.5 p-0 text-left leading-[0]">
            <div className="relative w-full shrink-0 font-['Manrope:Bold',_sans-serif] text-[16px] font-bold text-builder-primary">
              <p className="block leading-[24px]">Call-to-action button</p>
            </div>
            <div className="relative w-full shrink-0 font-['Manrope:Regular',_sans-serif] text-[14px] font-normal text-builder-secondary">
              <p className="block leading-[20px]">
                Guide visitors to the next step with a clear call-to-action button.
              </p>
            </div>
          </div>

          <div className="relative w-full shrink-0 rounded-lg bg-white">
            <label className="mb-1 block font-['Manrope:Bold',_sans-serif] text-[14px] font-bold text-builder-primary">
              Button text
            </label>
            <input
              type="text"
              value={formData.settings.buttonText}
              onChange={(e) => handleSettingsChange("buttonText", e.target.value)}
              placeholder="Get Started"
              className="relative box-border flex w-full flex-row content-stretch items-center justify-between text-clip rounded-lg border border-builder-border px-3 py-2 font-['Manrope:Regular',_sans-serif] text-[16px] font-normal leading-[24px] text-builder-primary focus:border-builder-accent focus:outline-none"
              required
            />
          </div>
          {errors.buttonText && (
            <div className="relative shrink-0 text-left font-['Manrope:Regular',_sans-serif] text-[13px] font-normal leading-[0] text-red-500">
              <p className="block leading-[19.5px]">{errors.buttonText}</p>
            </div>
          )}

          <div className="relative w-full shrink-0 rounded-lg bg-white">
            <label className="mb-1 block font-['Manrope:Bold',_sans-serif] text-[14px] font-bold text-builder-primary">
              Action
            </label>
            <select
              value={formData.settings.buttonAction}
              onChange={(e) => handleSettingsChange("buttonAction", e.target.value)}
              className="relative box-border w-full rounded-lg border border-builder-border bg-white px-3 py-2 font-['Manrope:Regular',_sans-serif] text-[16px] text-builder-primary focus:border-builder-accent focus:outline-none">
              {actionOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          {formData.settings.buttonAction === "page" && (
            <div className="relative w-full shrink-0 rounded-lg bg-white">
              <label className="mb-1 block font-['Manrope:Bold',_sans-serif] text-[14px] font-bold text-builder-primary">
                Select Page
              </label>
              <select
                value={formData.settings.ctaTarget || formData.settings.pageSlug || ""}
                onChange={(e) => handleSettingsChange("ctaTarget", e.target.value)}
                className="relative box-border w-full rounded-lg border border-builder-border bg-white px-3 py-2 font-['Manrope:Regular',_sans-serif] text-[16px] text-builder-primary focus:border-builder-accent focus:outline-none">
                <option value="">Select a page...</option>
                {pages && pages.length > 0 ? (
                  pages.map((page) => (
                    <option key={page.id} value={page.slug}>
                      {page.title}
                    </option>
                  ))
                ) : (
                  <option value="" disabled>
                    No pages available
                  </option>
                )}
              </select>
            </div>
          )}
          {formData.settings.buttonAction === "external" && (
            <div className="relative w-full shrink-0 rounded-lg bg-white">
              <label className="mb-1 block font-['Manrope:Bold',_sans-serif] text-[14px] font-bold text-builder-primary">
                External URL
              </label>
              <input
                type="url"
                value={formData.settings.buttonUrl || ""}
                onChange={(e) => handleSettingsChange("buttonUrl", e.target.value)}
                placeholder="https://example.com"
                className="relative box-border w-full rounded-lg border border-builder-border px-3 py-2 font-['Manrope:Regular',_sans-serif] text-[16px] text-builder-primary focus:border-builder-accent focus:outline-none"
              />
            </div>
          )}
          {formData.settings.buttonAction === "phone" && (
            <div className="relative w-full shrink-0 rounded-lg bg-white">
              <label className="mb-1 block font-['Manrope:Bold',_sans-serif] text-[14px] font-bold text-builder-primary">
                Phone Number
              </label>
              <input
                type="tel"
                value={formData.settings.phoneNumber || ""}
                onChange={(e) => handleSettingsChange("phoneNumber", e.target.value)}
                placeholder="(555) 555-5555"
                className="relative box-border w-full rounded-lg border border-builder-border px-3 py-2 font-['Manrope:Regular',_sans-serif] text-[16px] text-builder-primary focus:border-builder-accent focus:outline-none"
              />
            </div>
          )}
          {formData.settings.buttonAction === "email" && (
            <div className="relative w-full shrink-0 rounded-lg bg-white">
              <label className="mb-1 block font-['Manrope:Bold',_sans-serif] text-[14px] font-bold text-builder-primary">
                Email
              </label>
              <input
                type="email"
                value={formData.settings.email || ""}
                onChange={(e) => handleSettingsChange("email", e.target.value)}
                placeholder="hello@example.com"
                className="relative box-border w-full rounded-lg border border-builder-border px-3 py-2 font-['Manrope:Regular',_sans-serif] text-[16px] text-builder-primary focus:border-builder-accent focus:outline-none"
              />
            </div>
          )}
        </div>

        {/* Background image uploader - match CTAEditor styling */}
        <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-6 px-0 pb-6 pt-0">
          <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-0.5 p-0 text-left leading-[0]">
            <div className="relative w-full shrink-0 font-['Manrope:Bold',_sans-serif] text-[16px] font-bold text-builder-primary">
              <p className="block leading-[24px]">Background image</p>
            </div>
            <div className="relative w-full shrink-0 font-['Manrope:Regular',_sans-serif] text-[14px] font-normal text-builder-secondary">
              <p className="block leading-[20px]">
                Select a striking image that sets the tone and draws visitors into your website.
              </p>
            </div>
          </div>
          {formData.settings.backgroundImageUrl ? (
            <div className="mb-6 flex items-center justify-center">
              <div className="flex items-center gap-4">
                <img
                  src={formData.settings.backgroundImageUrl}
                  alt="Background preview"
                  className="size-16 rounded-lg object-cover"
                />
                <div className="flex flex-col gap-2">
                  <ImagePickerV2
                    practice={practice}
                    onImageSelect={handleBackgroundImageSelect}
                    fetching={uploadFetching}
                    showCurateTab={true}
                    currentImageUrl={formData.settings.backgroundImageUrl}
                    trigger={
                      <button className="font-bold text-builder-accent hover:underline" disabled={uploadFetching}>
                        Edit image
                      </button>
                    }
                  />
                  <button
                    className="font-bold text-red-500 hover:underline"
                    onClick={handleBackgroundImageRemove}
                    disabled={uploadFetching}>
                    Remove
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <ImagePickerV2
              practice={practice}
              onImageSelect={handleBackgroundImageSelect}
              fetching={uploadFetching}
              showCurateTab={true}
              currentImageUrl={formData.settings.backgroundImageUrl}
              trigger={
                <div className="relative box-border flex w-full shrink-0 cursor-pointer flex-col content-stretch items-center justify-center gap-1.5 rounded-lg border-2 border-dashed border-builder-border bg-white p-6 transition-colors hover:border-builder-accent">
                  <div className="relative flex size-10 shrink-0 items-center justify-center text-clip rounded-[100px] bg-builder-bg-light">
                    <svg className="size-5 text-builder-accent" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                      />
                    </svg>
                  </div>
                  <div className="relative shrink-0 text-nowrap text-center font-['Manrope:Bold',_sans-serif] text-[16px] font-bold leading-[0] text-builder-accent">
                    <p className="whitespace-pre leading-[24px]">
                      <span className="font-['Manrope:Bold',_sans-serif] font-bold">upload an image</span>
                    </p>
                  </div>
                  <div className="relative shrink-0 text-nowrap text-left font-['Manrope:Regular',_sans-serif] text-[13px] font-normal leading-[0] text-builder-secondary">
                    <p className="block whitespace-pre leading-[19.5px]">Supported formats: PNG, JPG, SVG</p>
                  </div>
                </div>
              }
            />
          )}
        </div>

        {/* Design settings */}
        <div className="relative box-border flex w-full max-w-[432px] shrink-0 flex-col content-stretch items-start justify-start gap-6 p-0">
          <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-0.5 p-0 text-left leading-[0]">
            <div className="relative w-full shrink-0 font-['Manrope:Bold',_sans-serif] text-[16px] font-bold text-builder-primary">
              <p className="block leading-[24px]">Design settings</p>
            </div>
          </div>
          <div className="grid w-full grid-cols-2 gap-4">
            {/* Background Color - Commented out - now using bgLight from theme
            <ColorInput
              label="Background Color"
              value={formData.settings.backgroundColor}
              onChange={(value) => handleSettingsChange("backgroundColor", value)}
            />
            */}
            <ColorInput
              label="Title Color"
              value={formData.settings.titleColor}
              onChange={(value) => handleSettingsChange("titleColor", value)}
            />
          </div>
          <div className="grid w-full grid-cols-2 gap-4">
            <ColorInput
              label="Subheadline Color"
              value={formData.settings.subheadlineColor}
              onChange={(value) => handleSettingsChange("subheadlineColor", value)}
            />
            <ColorInput
              label="Description Color"
              value={formData.settings.descriptionColor}
              onChange={(value) => handleSettingsChange("descriptionColor", value)}
            />
          </div>
          <div className="space-y-2">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={formData.settings.showIcon}
                onChange={(e) => handleSettingsChange("showIcon", e.target.checked)}
                className="rounded border-builder-border text-builder-accent focus:ring-builder-accent"
              />
              <span className="font-['Manrope:Regular',_sans-serif] text-[16px] text-builder-primary">
                Show icon in button
              </span>
            </label>
          </div>
          {formData.settings.showIcon && (
            <div className="relative w-full shrink-0 rounded-lg bg-white">
              <label className="mb-1 block font-['Manrope:Bold',_sans-serif] text-[14px] font-bold text-builder-primary">
                Icon Type
              </label>
              <select
                value={formData.settings.iconType}
                onChange={(e) => handleSettingsChange("iconType", e.target.value)}
                className="relative box-border w-full rounded-lg border border-builder-border bg-white px-3 py-2 font-['Manrope:Regular',_sans-serif] text-[16px] text-builder-primary focus:border-builder-accent focus:outline-none">
                {iconOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
      </div>

      <EditorFooter onDelete={onDelete} onCancel={handleCancelClick} onSave={handleSubmit} showDelete={isEditing} />
    </>
  )
}

export default CTABannerEditor
